/**
 * ToolModeDropdown.jsx
 * Composant de sélection du mode de traitement des messages
 * - Mode automatique (par défaut) : L'IA choisit le meilleur outil
 * - Modes forcés : Recherche web, analyse de sources, base de connaissances
 */

import React, { useState, useRef, useEffect } from 'react';
import { Bot, Globe, FileText, Database, ChevronDown, CheckCircle, Sparkles } from 'lucide-react';

const ToolModeDropdown = ({
  selectedTool = 'auto',
  onToolChange,
  sourcesCount = 0,
  disabled = false,
  compactMode = false  // ✨ NOUVEAU : Mode compact pour mobile
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Définition des outils disponibles avec labels courts pour mobile
  const tools = [
    { 
      id: 'auto', 
      label: compactMode ? 'Auto' : 'Automatique', 
      shortLabel: 'Auto',
      icon: Sparkles, 
      description: 'L\'IA choisit le meilleur outil',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverColor: 'hover:bg-purple-100'
    },
    { 
      id: 'web_search', 
      label: compactMode ? 'Web' : 'Recherche Web', 
      shortLabel: 'Web',
      icon: Globe, 
      description: 'Chercher sur internet',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      hoverColor: 'hover:bg-blue-100'
    },
    { 
      id: 'source_analysis', 
      label: compactMode ? 'Sources' : 'Analyser Sources', 
      shortLabel: 'Sources',
      icon: FileText, 
      description: sourcesCount > 0 ? `Module d'inspiration complet (${sourcesCount} source${sourcesCount > 1 ? 's' : ''})` : 'Module d\'inspiration complet',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      hoverColor: 'hover:bg-green-100',
      disabled: false // Toujours activé car c'est un module complet
    },
    { 
      id: 'knowledge_base', 
      label: compactMode ? 'Database' : 'Base de Connaissances', 
      shortLabel: 'Database',
      icon: Database, 
      description: 'Documents sauvegardés',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      hoverColor: 'hover:bg-orange-100'
    }
  ];

  const currentTool = tools.find(t => t.id === selectedTool) || tools[0];

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToolSelect = (toolId) => {
    const tool = tools.find(t => t.id === toolId);
    if (tool && !tool.disabled) {
      onToolChange(toolId);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton principal */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg transition-all duration-200 ${
          disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:bg-gray-50 hover:border-gray-400 active:scale-95'
        }`}
        title={currentTool.description}
      >
        <currentTool.icon size={16} className={currentTool.color} />
        <span className={`text-sm font-medium text-gray-700 ${
          compactMode ? 'inline' : 'hidden sm:inline'
        }`}>
          {currentTool.label}
        </span>
        <ChevronDown 
          size={14} 
          className={`text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Menu déroulant */}
      {isOpen && (
        <div className="absolute top-full mt-2 left-0 sm:left-auto sm:right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-[280px] sm:w-[280px] max-w-[calc(100vw-1rem)] sm:min-w-[280px] animate-slide-in-right">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
              Mode de traitement
            </div>
            
            {tools.map((tool) => {
              const Icon = tool.icon;
              const isSelected = tool.id === selectedTool;
              const isDisabled = tool.disabled;
              
              return (
                <button
                  key={tool.id}
                  onClick={() => handleToolSelect(tool.id)}
                  disabled={isDisabled}
                  className={`w-full flex items-start gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    isDisabled 
                      ? 'opacity-40 cursor-not-allowed' 
                      : isSelected
                      ? `${tool.bgColor} ring-2 ring-offset-1 ring-${tool.color.split('-')[1]}-300`
                      : `${tool.hoverColor} hover:shadow-sm`
                  }`}
                >
                  <div className={`mt-0.5 ${tool.color}`}>
                    <Icon size={18} />
                  </div>
                  
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-sm text-gray-800 flex items-center gap-2">
                      {tool.label}
                      {isSelected && (
                        <CheckCircle size={14} className={tool.color} />
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                      {tool.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer avec info */}
          <div className="border-t border-gray-200 px-3 py-2 bg-gray-50 rounded-b-lg">
            <p className="text-xs text-gray-600">
              💡 En mode automatique, l'IA analyse votre question et choisit le meilleur outil
            </p>
          </div>
        </div>
      )}

      {/* Styles pour l'animation */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ToolModeDropdown;
